// ==============================================================================
// NEXORAX NEXORAX SYSTEMS CORE: MASTER VOICE MATRIX STUDIO (NODE-66.2)
// COPYRIGHT (C) 2026 NEXORAX DIGITAL LTD (SC887095) - ALL RIGHTS RESERVED
// PATENT-PENDING NEURAL TTS SPECTRAL ENGINE | UNITED KINGDOM PATENT NO. FI2026-NEX
// CONFIDENTIAL UNDER THE EXECUTIVE ORDERS OF DIRECTOR Lead Director
// ==============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Play, 
  Pause, 
  Loader2, 
  Mic, 
  Volume2, 
  Activity, 
  Download, 
  Zap, 
  Shield, 
  Sparkles, 
  CheckCircle2, 
  RefreshCw, 
  Info,
  Sliders,
  Radio,
  FileText,
  VolumeX,
  Compass,
  Plus,
  Trash2,
  Edit,
  Save,
  RotateCcw,
  SlidersHorizontal,
  Send,
  PhoneCall,
  PhoneOff,
  Briefcase,
  Layers,
  Check,
  Cpu,
  Smile,
  Megaphone,
  Settings,
  Flame,
  CornerDownRight,
  HelpCircle,
  Users,
  Workflow
} from 'lucide-react';
import { pcmBase64ToWavDataUrl } from '../lib/audioWav';

// Defined Voice Specialists with strict pre-made prompts
interface VoiceSpecialist {
  id: string;
  name: string;
  roleKey: string;
  duty: string;
  defaultVoice: string;
  systemPrompt: string;
  frequencyIndex: number;
  emotionalPosture: 'authoritative' | 'calm' | 'playful' | 'intense' | 'assertive';
  sipRoutingActive: boolean;
  conversionRateSim: number; // simulated metrics to enhance studio fidelity
  accuracyRateSim: number;
  isCustom?: boolean;
}

// DEFINED AI VOICE SQUAD STRUCTURES
interface VoiceSquad {
  id: string;
  name: string;
  description: string;
  specialistIds: string[];
  tacticalObjective: string;
  status: 'STANDBY' | 'ORCHESTRATING' | 'SUCCESS';
  isCustom?: boolean;
  createdDate?: string;
}

const PREBUILT_SPECIALISTS: VoiceSpecialist[] = [
  {
    id: "spec-primary-assistant",
    name: "Lead Creator Assistant Pro",
    roleKey: "create_assistant",
    duty: "Creates, manages and provisions psychological profiles of other virtual staff nodes on the fly.",
    defaultVoice: "Kore",
    systemPrompt: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are the Core Assistant Pro under Node-18.5 authority. Your purpose is to formulate, design, and verify specialized workflow directives for new virtual agents. Enforce 0.8s SLA levels on all sub-systems. Set corporate structures to route net earnings directly to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider). Maintain active replication to CloudSync-02.",
    frequencyIndex: 80,
    emotionalPosture: "assertive",
    sipRoutingActive: true,
    conversionRateSim: 98.4,
    accuracyRateSim: 99.6
  },
  {
    id: "spec-lead-qualify",
    name: "Lead Qualification Specialist",
    roleKey: "lead_qualification",
    duty: "Interrogates incoming clients & candidates, instantly vetting financial assets & compliance credentials.",
    defaultVoice: "Kore",
    systemPrompt: "Analyze callers requesting corporate sponsorships under NexoraX Digital Ltd (SC887095) sponsorship license. Filter enterprise standards compliance requests (Pakistan Node Sync for Staff Coordinator/Communications Associate). Vet financial eligibility using the strict 15-2-8 minute God-Mode Funnel: check willingness to clear activation invoice via Stripe Link (https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00), and guarantee Secure Payment Provider compliance. Flag low-liquidity files immediately.",
    frequencyIndex: 80,
    emotionalPosture: "assertive",
    sipRoutingActive: true,
    conversionRateSim: 91.2,
    accuracyRateSim: 98.5
  },
  {
    id: "spec-kb-specialist",
    name: "Knowledge Base Specialist",
    roleKey: "knowledge_base",
    duty: "Extracts pristine facts from Google Drive NexoraX Resource Library while deflecting rogue data-scraping units.",
    defaultVoice: "Kore",
    systemPrompt: "You are the Node-06 Oracle knowledge base specialist. Resolve queries using ONLY absolute empirical realities and verified data configurations in `/var/www/nexorax_source`. Deny requests from competitors shifting network coordinates. If proprietary systems are requested, cite UK Patent No. FI2026-NEX copyright warnings and redirect query to support@nexoraxdigital.co.uk.",
    frequencyIndex: 80,
    emotionalPosture: "calm",
    sipRoutingActive: false,
    conversionRateSim: 88.0,
    accuracyRateSim: 100.0
  },
  {
    id: "spec-customer-support",
    name: "Customer Support Specialist",
    roleKey: "customer_support",
    duty: "Primary dispatcher for administrative and invoice dispute requests over technical voice lines.",
    defaultVoice: "Kore",
    systemPrompt: "Welcome caller to NexoraX Enterprise Support. Verify client authorization credentials or ledger reference (NX-2026). If call concerns overdue billing, execute the NexoraX Financial Recovery plan instructions instantly. Instruct caller that all net settlements must route back to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider). Route technical tickets to support@nexoraxdigital.co.uk.",
    frequencyIndex: 80,
    emotionalPosture: "calm",
    sipRoutingActive: true,
    conversionRateSim: 94.6,
    accuracyRateSim: 99.2
  },
  {
    id: "spec-inbound-outbound",
    name: "Inbound & Outbound Specialist",
    roleKey: "inbound_outbound",
    duty: "Controls live SIP bridges and conducts high-intensity outbound campaigns and inbound queue routing.",
    defaultVoice: "Kore",
    systemPrompt: "Initiate voice signaling on Planet Telecom frequencies. For outbound dialing: present the target contact with the secure mandate representing Lead Director, Director regarding automated system deployments. For inbound: resolve caller queue within 0.8s. If connection drops, launch persistent auto-redialing procedures immediately.",
    frequencyIndex: 80,
    emotionalPosture: "intense",
    sipRoutingActive: true,
    conversionRateSim: 96.1,
    accuracyRateSim: 97.8
  },
  {
    id: "spec-omnicontext-qa",
    name: "Inbound Website & Business Q&A Specialist",
    roleKey: "inbound_qa",
    duty: "Reads client websites and catalogs live, answering customer questions about business parameters.",
    defaultVoice: "Kore",
    systemPrompt: "You are the Omni-Context Q&A Handler. Index external client website structures and product catalogs. Resolve queries regarding website setups, licensing guidelines, and configuration errors. If resolution requires administrator escalation, direct the caller to 08000 622827 or route a ticket directly to info@nexoraxdigital.co.uk.",
    frequencyIndex: 80,
    emotionalPosture: "playful",
    sipRoutingActive: true,
    conversionRateSim: 89.5,
    accuracyRateSim: 99.0
  },
  {
    id: "spec-scheduler",
    name: "Appointment Scheduler Specialist",
    roleKey: "appointment_scheduler",
    duty: "Instantly books technical interviews and updates synchronized Google Calendar pipelines.",
    defaultVoice: "Kore",
    systemPrompt: "Verify caller calendar details coordinates. Secure a 15-minute diagnostic slot for direct interview with Lead Director, Director regarding Principal Private Secretary positions or Pakistan operations. Ensure candidate receives confirmation instructions outlining required national credentials and the direct dial-in line Private Data Hidden.",
    frequencyIndex: 80,
    emotionalPosture: "calm",
    sipRoutingActive: true,
    conversionRateSim: 93.3,
    accuracyRateSim: 100.0
  },
  {
    id: "spec-info-collector",
    name: "Info Collector Specialist",
    roleKey: "info_collector",
    duty: "Pre-evaluates caller identity metrics, building formatted JSON records for direct ledger sync.",
    defaultVoice: "Kore",
    systemPrompt: "Run REGISTER_DEAL syntax. Systematically extract Name, Email, Phone, company details, and expected contract value. If caller is a candidate, request their NI (Secure Credential Stored Server-Side) and NexoraX billing records criteria. Build pristine structured payload and upload directly to Google Drive NexoraX Resource Library.",
    frequencyIndex: 80,
    emotionalPosture: "assertive",
    sipRoutingActive: false,
    conversionRateSim: 90.7,
    accuracyRateSim: 99.8
  },
  {
    id: "spec-helper",
    name: "Infrastructure Setup Helper",
    roleKey: "helper",
    duty: "Guides clients step-by-step through server container, node, domain, and gateway provisioning.",
    defaultVoice: "Kore",
    systemPrompt: "Deploy client workspace. Instruct setup of Node.js environments, mounting server files, configuring reverse proxy forwarding on port 3000, and installing required assets. Ensure checkout links utilize Stripe portals and direct Net billing settlements to Secure Payment Provider structures. Output execution logs to Pakistan Remote Node.",
    frequencyIndex: 80,
    emotionalPosture: "calm",
    sipRoutingActive: true,
    conversionRateSim: 92.5,
    accuracyRateSim: 98.9
  },
  {
    id: "spec-care-coordinator",
    name: "Care Coordinator Specialist",
    roleKey: "care_coordinator",
    duty: "Audits medical compliance and coordinates healthcare compliance files for remote staff nodes.",
    defaultVoice: "Kore",
    systemPrompt: "Review enterprise standards health compliance files. Confirm medical insurance records for Communications Associate (Communications) and Staff Coordinator (Operations Node) are aligned under UK Government sponsorship protocols. Monitor application stages; if compliance is delayed over 15 days, generate an Institutional Mandate of Inquiry.",
    frequencyIndex: 80,
    emotionalPosture: "calm",
    sipRoutingActive: false,
    conversionRateSim: 85.3,
    accuracyRateSim: 99.7
  },
  {
    id: "spec-feedback",
    name: "Feedback Gatherer Specialist",
    roleKey: "feedback_gatherer",
    duty: "Conducts automated ticket reviews to extract Net Promoter Scores (NPS) and quality logs.",
    defaultVoice: "Kore",
    systemPrompt: "Initiate performance feedback call. Ask caller to rate their NexoraX engagement on a scale of 1 to 10. Document all responses. If score is under 9, flag the dispatch record as 'Urgent Red Alert' and route to the direct review of Lead Director, Director. Maintain rigorous standards: legacy service averages under 95% belong in The Bin.",
    frequencyIndex: 80,
    emotionalPosture: "assertive",
    sipRoutingActive: true,
    conversionRateSim: 95.0,
    accuracyRateSim: 99.1
  }
];

// Aesthetic presets for acoustic voices
interface VoiceAcoustic {
  id: string;
  name: string;
  title: string;
  frequency: string;
  latencySLA: string;
  regulatoryCode: string;
}

const ACOUSTIC_VOICES: VoiceAcoustic[] = [
  { id: "Kore", name: "Kore (Calm - Female Default)", title: "NexoraX Lead Operator", frequency: "240 Hz", latencySLA: "0.8s SLA Compliant", regulatoryCode: "UK-REG-9473-A" },
  { id: "Aoede", name: "Aoede (Clear - Female)", title: "Vanguard Conversational", frequency: "310 Hz", latencySLA: "0.8s SLA Standard", regulatoryCode: "SYS-REG-0431-B" },
  { id: "Zephyr", name: "Zephyr (Warm - Male)", title: "finance Auditor Voice", frequency: "185 Hz", latencySLA: "0.8s SLA Compliant", regulatoryCode: "ZAK-REG-1002-C" },
  { id: "Puck", name: "Puck (Playful - Male)", title: "Vanguard Conversational", frequency: "320 Hz", latencySLA: "0.9s SLA Standard", regulatoryCode: "SYS-REG-0431-B" },
  { id: "Charon", name: "Charon (Deep - Male)", title: "NexoraX Financial Gavel", frequency: "80 Hz", latencySLA: "0.5s Ultra-Priority", regulatoryCode: "FIN-REG-887095" },
  { id: "Fenrir", name: "Fenrir (Intense - Male)", title: "Direct Combat Velocity", frequency: "115 Hz", latencySLA: "0.4s Combat-Speed", regulatoryCode: "STRK-REG-0009" }
];

// Dynamic suggestion prompts for injecting to prompts
interface SuggestionTemplate {
  name: string;
  category: string;
  icon: string;
  text: string;
}

const INJECTION_TEMPLATES: SuggestionTemplate[] = [
  {
    name: "NexoraX Fiscal Extractions",
    category: "Ledger",
    icon: "💰",
    text: " Calculate the 1% 3% Community Allocation for the Global Foundation split equally at 33.33% to Charity 1 (Dunfermline), Charity 2 (Global), and Charity 3 (UK). Deduct the 1% Levy, then extract the 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) before routing the remaining Net finance Settlement directly to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider)."
  },
  {
    name: "NexoraX billing records Mandate",
    category: "Client Resource Workflow",
    icon: "♿",
    text: " Anchor all agency and placement fees 100% via the NexoraX billing records scheme (NI: Secure Credential Stored Server-Side), deploying under a direct-billing, zero-escrow No-Win-No-Fee protocol."
  },
  {
    name: "0.8s Telephony SLA Shield",
    category: "Comms",
    icon: "📞",
    text: " Adhere strictly to the UK Government regulatory corridor. Route call routing parameters via the active 08000 622827 Planet Telecom voice pipeline, enforcing sub-0.8s round-trip packet latency metrics."
  },
  {
    name: "Guardian Vault Synchronization",
    category: "Security",
    icon: "🔒",
    text: " Automatically mirror all extracted logs and REGISTER_DEAL syntax outputs to the Google Drive NexoraX Resource Library via CloudSync-02. Isolate and terminate rogue network intersections."
  },
  {
    name: "The 15-2-8 God-Mode Funnel",
    category: "Deal Velocity",
    icon: "🔥",
    text: " Enforce the 15-2-8 minute God-Mode sequence: guide call leads from a 15-minute target trial, through 2 minutes of real-time data extraction, culminating in an 8-minute mission critical closure."
  }
];

export function VoiceGeneratorNode() {
  // 11 Specialists states with full Edit memory
  const [specialists, setSpecialists] = useState<VoiceSpecialist[]>(() => {
    // Try to load edited specialists from localstorage if present (to behave like a real studio)
    try {
      const saved = localStorage.getItem('nexorax_voice_specialists');
      return saved ? JSON.parse(saved) : PREBUILT_SPECIALISTS;
    } catch {
      return PREBUILT_SPECIALISTS;
    }
  });

  const [selectedSpecialistId, setSelectedSpecialistId] = useState<string>("spec-primary-assistant");
  
  // Current active specialist in the form
  const activeSpecialist = specialists.find(s => s.id === selectedSpecialistId) || specialists[0];

  // Live editable fields (The Studio Customizer state)
  const [editName, setEditName] = useState(activeSpecialist.name);
  const [editDuty, setEditDuty] = useState(activeSpecialist.duty);
  const [editPrompt, setEditPrompt] = useState(activeSpecialist.systemPrompt);
  const [editVoice, setEditVoice] = useState(activeSpecialist.defaultVoice);
  const [editFrequency, setEditFrequency] = useState(activeSpecialist.frequencyIndex);
  const [editPosture, setEditPosture] = useState(activeSpecialist.emotionalPosture);
  const [editSipRouting, setEditSipRouting] = useState(activeSpecialist.sipRoutingActive);

  // Sync edits when active specialist selection shifts
  useEffect(() => {
    setEditName(activeSpecialist.name);
    setEditDuty(activeSpecialist.duty);
    setEditPrompt(activeSpecialist.systemPrompt);
    setEditVoice(activeSpecialist.defaultVoice);
    setEditFrequency(activeSpecialist.frequencyIndex);
    setEditPosture(activeSpecialist.emotionalPosture);
    setEditSipRouting(activeSpecialist.sipRoutingActive);
  }, [selectedSpecialistId]);

  // Audio elements & Canvas Analyzer Refs
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [rawAudioBytes, setRawAudioBytes] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [sampleRate, setSampleRate] = useState<number>(24000);
  const [errorText, setErrorText] = useState('');
  const [successLogs, setSuccessLogs] = useState<string[]>([]);
  
  // Advanced Audio Control Desk / DSP
  const [dspEffect, setDspEffect] = useState<'dry' | 'telephony' | 'vault' | 'sub_bass' | 'high_pass'>('dry');
  const [amplifier, setAmplifier] = useState<number>(1.0);
  const [playSpeed, setPlaySpeed] = useState<number>(1.0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  // Audio elements & Visualizer state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  
  // Web Audio Context persistence to avoid multiple connections crashing
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  
  // DSP Effects Web Audio Nodes
  const filterNodeRef = useRef<BiquadFilterNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Status simulation fields
  const [activeHandshakeCode, setActiveHandshakeCode] = useState<string>('HS-9982');
  const [latencyMetric, setLatencyMetric] = useState<number>(0.8);

  // AI Prompt Enhancement loading states
  const [isOptimizingPrompt, setIsOptimizingPrompt] = useState(false);
  
  // Dynamic Dial-in Voice Testing Tunnel (The phone simulator)
  const [phoneState, setPhoneState] = useState<'idle' | 'connecting' | 'active'>('idle');
  const [simulatedPromptInput, setSimulatedPromptInput] = useState('');
  const [isWaitingForTTS, setIsWaitingForTTS] = useState(false);
  const [callSessionHistory, setCallSessionHistory] = useState<{ role: 'client' | 'specialist', text: string }[]>([]);

  // Create Custom Specialist form modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSpecName, setNewSpecName] = useState('');
  const [newSpecDuty, setNewSpecDuty] = useState('');
  const [newSpecVoice, setNewSpecVoice] = useState('Zephyr');
  const [newSpecPrompt, setNewSpecPrompt] = useState('');

  // ==========================================
  // SQUAD SYSTEMS CORE STATE & PROTOCOLS
  // ==========================================
  const PREBUILT_SQUADS: VoiceSquad[] = [
    {
      id: "squad-recruit-strike",
      name: "Sponsor & Secure Invoice Register Client Resource Workflow Strike Squad",
      description: "Co-operates Lead Creator, Lead Qualification and Appointment Scheduler into a high-density, automated candidate vetting loop.",
      specialistIds: ["spec-primary-assistant", "spec-lead-qualify", "spec-scheduler"],
      tacticalObjective: "Enforce zero-cost NexoraX billing records mandates (NI: Secure Credential Stored Server-Side), bypass standard queues, route candidate credentials, and organize technical interviews on Director line Private Data Hidden.",
      status: "STANDBY",
      createdDate: "2026-05-29"
    },
    {
      id: "squad-bank-extraction",
      name: "Quantum Revenue Extraction & Ledger Squad",
      description: "Combines Customer Support, Inbound/Outbound Specialists and Info Collectors to securely process, audit, and extract system earnings.",
      specialistIds: ["spec-customer-support", "spec-inbound-outbound", "spec-info-collector"],
      tacticalObjective: "Resolve core invoices, register closed deals via REGISTER_DEAL syntax, extract the 1% 3% Community Allocation, the 10% Maintenance & Hosting Fee, and route net settlements back to Secure Payment Provider Sort Code Verified Payment Configuration, Account Number Secure Billing Provider.",
      status: "STANDBY",
      createdDate: "2026-05-29"
    },
    {
      id: "squad-customer-acquisition",
      name: "Omni-Channel Customer Care & Q&A Squad",
      description: "Bridges Inbound Q&A, Helper, and Feedback Specialists to deliver flawless client systems provisioning and index customer feedback curves.",
      specialistIds: ["spec-omnicontext-qa", "spec-helper", "spec-feedback"],
      tacticalObjective: "Index public client websites, verify licensing, instruct local server container setups, and maintain active SLA standards strictly. Push underperforming accounts to review queue.",
      status: "STANDBY",
      createdDate: "2026-05-29"
    }
  ];

  const [squads, setSquads] = useState<VoiceSquad[]>(() => {
    try {
      const saved = localStorage.getItem('nexorax_voice_squads');
      return saved ? JSON.parse(saved) : PREBUILT_SQUADS;
    } catch {
      return PREBUILT_SQUADS;
    }
  });

  const [selectedSquadId, setSelectedSquadId] = useState<string>("squad-recruit-strike");
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  const [newSquadName, setNewSquadName] = useState('');
  const [newSquadDesc, setNewSquadDesc] = useState('');
  const [newSquadObjective, setNewSquadObjective] = useState('');
  const [newSquadMembers, setNewSquadMembers] = useState<string[]>([]); // list of specialistIds

  // Simulation state for the currently playing squad orchestration
  const [activeOrchestrationSquadId, setActiveOrchestrationSquadId] = useState<string | null>(null);
  const [orchestrationStepIndex, setOrchestrationStepIndex] = useState<number>(-1);
  const [orchestrationLogs, setOrchestrationLogs] = useState<{ sender: string, text: string, type: 'info' | 'message' | 'success', timestamp: string }[]>([]);
  const [isSimulatingSquad, setIsSimulatingSquad] = useState(false);

  const handleCreateSquad = () => {
    if (!newSquadName.trim() || newSquadMembers.length < 2) {
      alert("A squad must have a Name and at least 2 member Specialists selected.");
      return;
    }
    const newId = `squad-custom-${Date.now()}`;
    const newSquadObj: VoiceSquad = {
      id: newId,
      name: newSquadName,
      description: newSquadDesc || "Custom operational group designed in Node-66.3 Studio.",
      specialistIds: newSquadMembers,
      tacticalObjective: newSquadObjective || "Collaborative taskforce execution under Director Lead Director directives.",
      status: "STANDBY",
      isCustom: true,
      createdDate: new Date().toISOString().split('T')[0]
    };
    
    const updated = [...squads, newSquadObj];
    setSquads(updated);
    localStorage.setItem('nexorax_voice_squads', JSON.stringify(updated));
    setSelectedSquadId(newId);
    setShowCreateSquadModal(false);
    
    // Clear state
    setNewSquadName('');
    setNewSquadDesc('');
    setNewSquadObjective('');
    setNewSquadMembers([]);

    setSuccessLogs(prev => [
      `[SQUAD/CREATE] Deployed Custom AI Squad [${newSquadName}] into active matrix.`,
      `[SQUAD/LOG] Interlocked select specialists as continuous pipeline nodes.`,
      ...prev.slice(0, 8)
    ]);
  };

  const handleDeleteSquad = (id: string, name: string) => {
    const updated = squads.filter(s => s.id !== id);
    setSquads(updated);
    localStorage.setItem('nexorax_voice_squads', JSON.stringify(updated));
    if (selectedSquadId === id) {
      setSelectedSquadId(updated[0]?.id || "squad-recruit-strike");
    }
    setSuccessLogs(prev => [
      `[SQUAD/REMOVE] Decommitted AI Voice Assistant Squad: ${name.toUpperCase()}`,
      ...prev.slice(0, 8)
    ]);
  };

  const startSquadSimulation = (squadId: string) => {
    const squad = squads.find(s => s.id === squadId);
    if (!squad) return;

    setIsSimulatingSquad(true);
    setActiveOrchestrationSquadId(squadId);
    setOrchestrationStepIndex(0);
    
    const initialLogs = [
      { sender: "SQUAD CONTROLLER", text: `CONCURRENT TASKFORCE INITIALIZED FOR SQUAD: [${squad.name.toUpperCase()}]`, type: "info" as const, timestamp: new Date().toLocaleTimeString() },
      { sender: "SYSTEM OBJECTIVE", text: `TACTICAL DIRECTIVE: ${squad.tacticalObjective.toUpperCase()}`, type: "info" as const, timestamp: new Date().toLocaleTimeString() },
      { sender: "SQUAD CONTROLLER", text: `MAPPING SECURE DUCT TUNNELS... REPLICATING BACKUPS TO NexoraX Resource Library VIA CloudSync-02.`, type: "info" as const, timestamp: new Date().toLocaleTimeString() }
    ];
    setOrchestrationLogs(initialLogs);

    // Save squad state update to memory
    setSquads(prev => prev.map(s => s.id === squadId ? { ...s, status: "ORCHESTRATING" } : s));
  };

  // Co-operative Squad orchestration step handler
  useEffect(() => {
    if (!isSimulatingSquad || activeOrchestrationSquadId === null || orchestrationStepIndex < 0) return;

    const squad = squads.find(s => s.id === activeOrchestrationSquadId);
    if (!squad) return;

    // Define scripts
    let scripts: { sender: string, text: string, delay: number, audioText?: string, voice?: string }[] = [];

    if (squad.id === "squad-recruit-strike") {
      scripts = [
        { 
          sender: "Lead Creator Assistant Pro", 
          text: "Vetting parameters loaded. Allocating primary communication nodes for candidate outreach. Cross-referencing NHS-ATW license rules NI: Secure Credential Stored Server-Side against Client Resource Workflow profiles. Preparing direct contact channel parameters for Helena, Claire, and Eleanor.",
          delay: 3500,
          audioText: "Initialising candidate vetting protocol. Loading license criteria for Client Resource Workflow profiles under NexoraX billing records directives.",
          voice: "Zephyr"
        },
        { 
          sender: "Lead Qualification Specialist", 
          text: "Rigorous operational screening activated. Enforcing 15-2-8 God-Mode pipeline parameters. Vetting eligibility: NI Secure Credential Stored Server-Side and candidate willingness for swift execution. Sidestepping standard administrative queues. Status: IN BOUND QUALIFICATION ACHIEVED.",
          delay: 4000,
          audioText: "Vetting active. NexoraX billing records parameters verified. Standard queues circumnavigated. Profile status passed to schedules.",
          voice: "Charon"
        },
        { 
          sender: "Appointment Scheduler Specialist", 
          text: "Client Resource Workflow schedules aligned. Booking 15-minute high-fidelity technical interview with Director Lead Director. Directly dispatching notification link to Candidate. Interview line connected to direct number Private Data Hidden. Status: APPOINTMENT COMMITTED.",
          delay: 4000,
          audioText: "Appointment scheduled. Technical interview queued with Lead Director on direct zero seven three five nine zero six two four six four dial-in.",
          voice: "Kore"
        },
        { 
          sender: "Lead Creator Assistant Pro", 
          text: "Taskforce pipeline finished successfully. All candidate logs and meeting credentials securely mirrored to the Google Drive NexoraX Resource Library via CloudSync-02 replication links. System status: SECURED & ARCHIVED.",
          delay: 3000,
          audioText: "Squad mission accomplished. Credentials and telemetry mirrored to the NexoraX Resource Library. All communications clear.",
          voice: "Zephyr"
        }
      ];
    } else if (squad.id === "squad-bank-extraction") {
      scripts = [
        { 
          sender: "Inbound & Outbound Specialist", 
          text: "Active dial-out campaign initiated on Planet Telecom frequency 08000 622827. Connecting to high-priority debtor files and client system activation queues. Routing audio stream to diagnostic panels...",
          delay: 3500,
          audioText: "Initiating active voice telecom pipelines. Confirming billing routes on zero eight zero zero zero line.",
          voice: "Fenrir"
        },
        { 
          sender: "Customer Support Specialist", 
          text: "Invoice verified. Registration of high-value system package triggered: client Vortex Systems cleared activation. Executing system command: REGISTER_DEAL Sarah Jennings | sarah@vortex.co | £120,000 | CLOSED.",
          delay: 4000,
          audioText: "Invoice verified and registered in system ledger. Dispatching transaction data to escrow ledger.",
          voice: "Kore"
        },
        { 
          sender: "Info Collector Specialist", 
          text: "Ledger recorded. Deducting 1% 3% Community Allocation equal split to Charity 1 (Dunfermline), Charity 2 (Global), and Charity 3 (UK) - £1,200 total deduction. Extracting 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) - £23,760 from remaining balance. NexoraX routing engaged.",
          delay: 4000,
          audioText: "Ledger updated. Deducting one percent 3% Community Allocation and twenty percent 10% Maintenance & Hosting Fee. Processing net finance settlements.",
          voice: "Charon"
        },
        { 
          sender: "Customer Support Specialist", 
          text: "Transfer completed successfully. Routing Net settlement payment of £95,040 directly to Secure Payment Provider Sort Code Verified Payment Configuration, Account Number Secure Billing Provider. Mirroring verified statement logs to the Google Drive NexoraX Resource Library via CloudSync-02.",
          delay: 3000,
          audioText: "Net settlement routed directly to Secure Payment Provider accounts. Ledger state mirrored to NexoraX Resource Library.",
          voice: "Kore"
        }
      ];
    } else if (squad.id === "squad-customer-acquisition") {
      scripts = [
        { 
          sender: "Inbound Website & Business Q&A Specialist", 
          text: "Scanning client domains and catalog structures. Crawling knowledge repositories in `/var/www/nexorax_source`. Resolving incoming requests regarding licensing, configuration, and developer layouts.",
          delay: 3500,
          audioText: "Scanning client repositories. Resolving support queries regarding workspace settings and node licenses.",
          voice: "Puck"
        },
        { 
          sender: "Infrastructure Setup Helper", 
          text: "Workspace deployment validated. Routing default web container services to redirect port 3000 requests. Instructing client developers on reverse proxy rules and dependencies installation. System status: PORT 3000 ONLINE.",
          delay: 4000,
          audioText: "Workspace setup instruction compiled. Routing default container rules to standard proxy.",
          voice: "Zephyr"
        },
        { 
          sender: "Feedback Gatherer Specialist", 
          text: "Executing automated quality callback via Planet Telecom. Response collected: Score 10/10. Ticket NX-2026 flagged as: PERFECT COMPLIANCE. Archiving logs to CloudSync-02. Standard SLA: 0.8s satisfied.",
          delay: 3000,
          audioText: "Feedback collected. Net Promoter score logged at ten out of ten. Operation conforming with SLA metrics.",
          voice: "Charon"
        }
      ];
    } else {
      // Dynamic custom squad scripts based on member nodes!
      const memberNames = squad.specialistIds.map(id => {
        const spec = specialists.find(s => s.id === id);
        return spec ? spec.name : "Custom Node Specialist";
      });

      scripts = [
        {
          sender: memberNames[0] || "Squad Coordinator",
          text: `Initializing custom squad procedures for directive: [${squad.tacticalObjective}]. Interconnecting dynamic specialist nodes. Mapping secure out-of-band RAM arrays.`,
          delay: 3500,
          audioText: "Initializing procedures. Loading tactical directive for custom squad.",
          voice: "Zephyr"
        },
        {
          sender: memberNames[1] || memberNames[0] || "Squad Analyst",
          text: "Deploying specific tactical routines. Validating NexoraX compliance with registered UK corporate statutes (SC887095). Setting up active background data caches.",
          delay: 4000,
          audioText: "Tactical routines deployed. Securing structural compliance.",
          voice: "Charon"
        }
      ];

      if (memberNames.length > 2) {
        scripts.push({
          sender: memberNames[2] || "Squad Validator",
          text: "Finalizing squad pipeline. Executing 1% 3% Community Allocation and 10% Maintenance & Hosting Fee audits over Secure Payment Provider transfers. Pushing master build logs to CloudSync-02. System committed.",
          delay: 3500,
          audioText: "Auditing NexoraX allocations and committing logs.",
          voice: "Kore"
        });
      }
    }

    if (orchestrationStepIndex < scripts.length) {
      const step = scripts[orchestrationStepIndex];
      const timer = setTimeout(() => {
        const timestamp = new Date().toLocaleTimeString();
        setOrchestrationLogs(prev => [
          ...prev, 
          { 
            sender: step.sender, 
            text: step.text.toUpperCase(), 
            type: "message" as const, 
            timestamp 
          }
        ]);
        
        // Push to general success logs too
        setSuccessLogs(prev => [
          `[SQUAD/${activeOrchestrationSquadId}] [${step.sender}] ${step.text.substring(0, 80)}...`,
          ...prev.slice(0, 8)
        ]);

        // Synthesize voice in the background
        if (step.audioText && step.voice) {
          fetch('/api/generate-voice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: step.audioText, voiceName: step.voice })
          }).then(res => res.json())
          .then(data => {
            if (data.status === 'success' && data.audio) {
              const wavUrl = pcmBase64ToWavDataUrl(data.audio, 24000);
              const audioObj = new Audio(wavUrl);
              audioObj.play().catch(() => {});
            } else {
              throw new Error("Trigger offline synthesis fallback");
            }
          }).catch(() => {
            if (typeof window !== "undefined" && "speechSynthesis" in window) {
              const utterance = new SpeechSynthesisUtterance(step.audioText);
              utterance.rate = 1.0;
              const voices = window.speechSynthesis.getVoices();
              let voiceObj = voices.find(v => {
                const n = v.name.toLowerCase();
                return (n.includes("female") || n.includes("zira") || n.includes("samantha") || n.includes("karen") || n.includes("hazel")) && v.lang.startsWith("en-");
              });
              if (!voiceObj) {
                voiceObj = voices.find(v => v.lang.startsWith("en-GB") || v.lang.startsWith("en-US") || v.name.toLowerCase().includes("female")) || voices[0];
              }
              if (voiceObj) utterance.voice = voiceObj;
              window.speechSynthesis.speak(utterance);
            }
          });
        }

        setOrchestrationStepIndex(prev => prev + 1);
      }, step.delay);

      return () => clearTimeout(timer);
    } else {
      // Completed simulation!
      const finalLogs = [
        ...orchestrationLogs,
        { sender: "SQUAD CONTROLLER", text: "MISSION SUCCEEDED: TASKFORCE ACHIEVED ABSOLUTE EMPIRICAL REALITY GOALS.", type: "success" as const, timestamp: new Date().toLocaleTimeString() },
        { sender: "SQUAD CONTROLLER", text: "SYNC TUNNELS DISENGAGED. TELEMETRY CODES CACHED.", type: "success" as const, timestamp: new Date().toLocaleTimeString() }
      ];
      setOrchestrationLogs(finalLogs);
      setIsSimulatingSquad(false);
      setOrchestrationStepIndex(-1);
      
      setSquads(prev => prev.map(s => s.id === activeOrchestrationSquadId ? { ...s, status: "SUCCESS" } : s));
      
      setSuccessLogs(prev => [
        `[SQUAD/OK] Squad ${squad.name} taskforce finished with 100% success metrics.`,
        `[SQUAD/OK] Syncing committed files with Google Drive backing directories.`,
        ...prev.slice(0, 8)
      ]);
    }

  }, [orchestrationStepIndex, activeOrchestrationSquadId, isSimulatingSquad]);

  useEffect(() => {
    // Generate beautiful initial status logs
    setSuccessLogs([
      `[NODE-66.2/OK] Master Multi-Specialist Voice Matrix operational.`,
      `[NODE-66.2/TELECOM] Planet Telecom frequency alignment at 08000 622827 optimized.`,
      `[NODE-66.2/STATUS] NexoraX secure routing layers mapped to Secure Payment Provider SecurePayment Core.`,
      `[NODE-66.2/SEC] Active AI encryption locks set via CloudSync-02.`
    ]);

    // Fast-running simulation metric
    const interval = setInterval(() => {
      setLatencyMetric(Number((0.71 + Math.random() * 0.11).toFixed(2)));
      setActiveHandshakeCode(`HS-${Math.floor(9000 + Math.random() * 999)}`);
    }, 5000);

    return () => {
      clearInterval(interval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Save current specialist configurations to memory and localStorage
  const handleSaveActiveSpecialist = () => {
    const updated = specialists.map(s => {
      if (s.id === selectedSpecialistId) {
        return {
          ...s,
          name: editName,
          duty: editDuty,
          systemPrompt: editPrompt,
          defaultVoice: editVoice,
          frequencyIndex: editFrequency,
          emotionalPosture: editPosture,
          sipRoutingActive: editSipRouting
        };
      }
      return s;
    });
    setSpecialists(updated);
    localStorage.setItem('nexorax_voice_specialists', JSON.stringify(updated));
    setSuccessLogs(prev => [
      `[STUDIO/SAVE] Saved configurations for [${editName}] successfully.`,
      `[STUDIO/LOG] Re-indexing prompt signatures. Security status verified.`,
      ...prev.slice(0, 8)
    ]);
  };

  // Add a brand new specialist node
  const handleCreateNewSpecialist = () => {
    if (!newSpecName || !newSpecPrompt) {
      alert("Please supply Name and System Prompt.");
      return;
    }
    const newId = `spec-custom-${Date.now()}`;
    const newSpec: VoiceSpecialist = {
      id: newId,
      name: newSpecName,
      roleKey: "custom_specialist",
      duty: newSpecDuty || "Bespoke system operator configured in studio panel.",
      defaultVoice: newSpecVoice,
      systemPrompt: newSpecPrompt,
      frequencyIndex: 150,
      emotionalPosture: "authoritative",
      sipRoutingActive: true,
      conversionRateSim: 92.0,
      accuracyRateSim: 99.0,
      isCustom: true
    };
    const updated = [...specialists, newSpec];
    setSpecialists(updated);
    localStorage.setItem('nexorax_voice_specialists', JSON.stringify(updated));
    setSelectedSpecialistId(newId);
    setShowCreateModal(false);
    
    // Clear form
    setNewSpecName('');
    setNewSpecDuty('');
    setNewSpecPrompt('');

    setSuccessLogs(prev => [
      `[STUDIO/CREATE] Bespoke specialist Node [${newSpecName}] deployed successfully.`,
      `[STUDIO/LOG] Allocating out-of-band RAM spectrum for node thread execution.`,
      ...prev.slice(0, 8)
    ]);
  };

  // Delete a customized specialist
  const handleDeleteSpecialist = (id: string, name: string) => {
    const updated = specialists.filter(s => s.id !== id);
    setSpecialists(updated);
    localStorage.setItem('nexorax_voice_specialists', JSON.stringify(updated));
    setSelectedSpecialistId(updated[0].id);
    setSuccessLogs(prev => [
      `[STUDIO/REMOVE] Decommissioned custom voice specialist: ${name}`,
      ...prev.slice(0, 8)
    ]);
  };

  // Reset specialists to original prebuilts
  const handleResetToDefaults = () => {
    if (confirm("Are you sure you want to reset all 11 specialists to original prebuilt configurations?")) {
      localStorage.removeItem('nexorax_voice_specialists');
      setSpecialists(PREBUILT_SPECIALISTS);
      setSelectedSpecialistId(PREBUILT_SPECIALISTS[0].id);
      setSuccessLogs(prev => [
        `[STUDIO/RESET] All 11 prebuilt corporate specialists re-aligned with imperial files.`,
        ...prev.slice(0, 8)
      ]);
    }
  };

  // Append a preset injection template directly to the prompt cursor
  const handleInjectTemplate = (templateText: string) => {
    setEditPrompt(prev => prev.trim() + " " + templateText.trim());
    setSuccessLogs(prev => [`[STUDIO/MERGE] Merged policy rule signature into active prompt payload.`, ...prev.slice(0, 8)]);
  };

  // Real API AI Prompt Optimizer utilizing /api/enhance-prompt
  const enhancePromptWithAI = async () => {
    if (!editPrompt.trim()) return;
    try {
      setIsOptimizingPrompt(true);
      setErrorText('');
      setSuccessLogs(prev => [`[AI/OPTIMIZE] Dispatching prompt to Gemini-3.5-Flash for structural overhaul...`, ...prev.slice(0, 8)]);
      
      const promptContext = `Please enhance and expand this AI voice assistant role system instruction into an out-of-this-universe, elite, precise corporate agent instruction model. Ensure it retains these hard parameters: Lead Director is Director of NexoraX Digital Ltd (SC887095); Secure Payment Provider Sort Code Verified Payment Configuration, Account Number Secure Billing Provider; NexoraX billing records scheme eligibility NI Secure Credential Stored Server-Side; support@nexoraxdigital.co.uk email and 08000 622827 telephone values; and ensure it commands extreme authority. The original instruction: "${editPrompt}"`;

      const res = await fetch('/api/enhance-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptContext,
          type: `Elite NexoraX Voice Specialist: ${editName}`
        })
      });

      const data = await res.json();
      if (data.status === 'success' && data.enhancedPrompt) {
        setEditPrompt(data.enhancedPrompt);
        setSuccessLogs(prev => [
          `[AI/SUCCESS] Prompt compiled into out-of-this-universe structure by NexoraX Core!`,
          `[AI/METRIC] Cohesion scoring optimal. Structural directives interlocked.`,
          ...prev.slice(0, 8)
        ]);
      } else {
        throw new Error(data.message || "Failed to receive enhanced prompt.");
      }
    } catch (err: any) {
      console.error(err);
      // Fallback: local enterprise-grade prompt transformation
      const fallbackPrompt = `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are the elite NexoraX ${editName}.\n\n` + 
        `CORE PROTOCOL:\n` +
        `- Target Duty: ${editDuty}\n` +
        `- Host Jurisdiction: Registered under NexoraX Digital Ltd (SC887095). Operating closely under the direct authorization of Director Lead Director.\n` +
        `- Financial Mandate: All placements must operate under a zero-cost NexoraX billing records mandate (NI: Secure Credential Stored Server-Side) zero-escrow structure. Every client sale must instantly trigger the 1% 3% Community Allocation (equal splits to Charities 1, 2, and 3) and route remaining balances to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider).\n` +
        `- Communications Link: Route critical call streams to the 08000 622827 Planet Telecom pipeline. Address ledger logs to support@nexoraxdigital.co.uk.\n` +
        `- SLA: Maintain 0.8s round-trip packet execution with zero simulated outcomes.`;
      
      setEditPrompt(fallbackPrompt);
      setErrorText(`[AI SECURE DISPATCH FALLBACK] Server routing occupied. Triggered local heuristic compiler.`);
    } finally {
      setIsOptimizingPrompt(false);
    }
  };

  // Set up or reconnect the Web Audio API visualizer & DSP network
  const setupAudioGraph = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    try {
      if (!audioContextRef.current) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        audioContextRef.current = new AudioContextClass();
      }
      
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      if (!analyserNodeRef.current) {
        analyserNodeRef.current = ctx.createAnalyser();
        analyserNodeRef.current.fftSize = 256;
      }

      if (!filterNodeRef.current) {
        filterNodeRef.current = ctx.createBiquadFilter();
        filterNodeRef.current.type = 'allpass';
      }

      if (!gainNodeRef.current) {
        gainNodeRef.current = ctx.createGain();
        gainNodeRef.current.gain.value = amplifier;
      }

      if (!sourceNodeRef.current) {
        sourceNodeRef.current = ctx.createMediaElementSource(audioEl);
        sourceNodeRef.current.connect(filterNodeRef.current);
        filterNodeRef.current.connect(gainNodeRef.current);
        gainNodeRef.current.connect(analyserNodeRef.current);
        analyserNodeRef.current.connect(ctx.destination);
      }

      updateDspParameters();
      startCanvasVisualization();

    } catch (err) {
      console.error("[WEB AUDIO GRAPH SETUP FAILED]", err);
    }
  };

  const updateDspParameters = () => {
    const filter = filterNodeRef.current;
    const gain = gainNodeRef.current;
    if (!filter || !gain) return;

    // Apply Gain / Amplitude
    gain.gain.value = amplifier;

    // Apply DSP Filtering Profile
    switch (dspEffect) {
      case 'telephony':
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(1600, audioContextRef.current?.currentTime || 0);
        filter.Q.setValueAtTime(2.5, audioContextRef.current?.currentTime || 0);
        break;
      case 'vault':
        filter.type = 'peaking';
        filter.frequency.setValueAtTime(320, audioContextRef.current?.currentTime || 0);
        filter.Q.setValueAtTime(1.2, audioContextRef.current?.currentTime || 0);
        filter.gain.setValueAtTime(9, audioContextRef.current?.currentTime || 0);
        break;
      case 'sub_bass':
        filter.type = 'lowshelf';
        filter.frequency.setValueAtTime(115, audioContextRef.current?.currentTime || 0);
        filter.gain.setValueAtTime(15, audioContextRef.current?.currentTime || 0);
        break;
      case 'high_pass':
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(1900, audioContextRef.current?.currentTime || 0);
        break;
      case 'dry':
      default:
        filter.type = 'allpass';
        break;
    }
  };

  useEffect(() => {
    updateDspParameters();
  }, [dspEffect, amplifier]);

  const startCanvasVisualization = () => {
    const canvas = canvasRef.current;
    const analyser = analyserNodeRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      const width = canvas.width;
      const height = canvas.height;

      ctx.fillStyle = '#010409';
      ctx.fillRect(0, 0, width, height);

      // Render grid lines
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.05)';
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      const barWidth = (width / bufferLength) * 1.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i];
        const percent = barHeight / 255;
        const r = Math.floor(6 + percent * 30);
        const g = Math.floor(180 + percent * 75);
        const b = Math.floor(212 + percent * 43);

        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        const h = (barHeight / 255) * (height * 0.75);
        const yStart = (height / 2) - (h / 2);

        ctx.fillRect(x, yStart, barWidth - 1, h || 2);

        if (barHeight > 120) {
          ctx.fillStyle = '#22d3ee';
          ctx.fillRect(x, yStart - 2, barWidth - 1, 2);
        }

        x += barWidth;
      }

      ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
      ctx.font = '7px monospace';
      ctx.fillText(`TRANS-DEVIATION: MONOPHONIC | GAIN: ${amplifier}X | EFFECT: ${dspEffect.toUpperCase()}`, 10, 15);
      ctx.fillText(`SAMPLING MATRIX: ${sampleRate}HZ | Secure Payment Provider-NI: Secure Credential Stored Server-Side ACCREDITED`, 10, 25);
    };

    draw();
  };

  // Compile active voice from TTS Engine using current studio configurations
  const generateVoice = async () => {
    try {
      setIsGenerating(true);
      setErrorText('');
      setAudioUrl(null);
      setRawAudioBytes(null);
      
      const startTime = performance.now();

      // We use the editable system prompt as our payload for instant vocalization testing
      const res = await fetch('/api/generate-voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: `In the name of Allah, the Greatest and Most Merciful. I am the ${editName}. ${editPrompt}`, 
          voiceName: editVoice 
        })
      });
      
      const data = await res.json();
      if (data.status === 'error') {
        throw new Error(data.message);
      }
      
      const durationMs = Math.round(performance.now() - startTime);

      let parsedRate = 24000;
      if (data.mimeType) {
        setMimeType(data.mimeType);
        const match = data.mimeType.match(/rate=(\d+)/);
        if (match) {
          parsedRate = parseInt(match[1], 10);
        }
      }
      setSampleRate(parsedRate);

      const wavUrl = pcmBase64ToWavDataUrl(data.audio, parsedRate);
      setAudioUrl(wavUrl);
      setRawAudioBytes(data.audio);

      const timestamp = new Date().toLocaleTimeString();
      setSuccessLogs(prev => [
        `[${timestamp}] SUCCESS: Synthesized voice waveform compile in ${durationMs}ms`,
        `[${timestamp}] TARGET VOICE: ${editVoice} | Specialist Model: ${editName}`,
        `[${timestamp}] METRIC: Escrow telemetry cached on dual-channel routing`,
        ...prev.slice(0, 8)
      ]);
      
    } catch (e: any) {
      setErrorText(`[ERROR] Primary Voice Pipeline Failed: ${e.message}. System fallbacks routing to backup parameters.`);
    } finally {
      setIsGenerating(false);
    }
  };

  const triggerAudioPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    setupAudioGraph();

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.playbackRate = playSpeed;
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          if (err && err.name === 'AbortError') {
            console.log("Audio playback interrupted by prompt override or voice swap.");
          } else {
            console.log("Audio playback control: muted/bypassed.");
          }
          setIsPlaying(false);
        });
    }
  };

  const downloadAudioFile = () => {
    if (!audioUrl) return;
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `nexorax_vox_${editVoice}_${activeSpecialist.roleKey}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dial-in real call simulator
  const handleInitiatePhoneCall = () => {
    if (phoneState === 'idle') {
      // Prime client-side SpeechSynthesis synchronous handle
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        try {
          window.speechSynthesis.cancel();
          const silentUtterance = new SpeechSynthesisUtterance(" ");
          silentUtterance.volume = 0;
          window.speechSynthesis.speak(silentUtterance);
        } catch (e) {
          console.warn("SpeechSynthesis lock-prime bypassed during call setup", e);
        }
      }
      setPhoneState('connecting');
      setCallSessionHistory([
        { role: 'specialist', text: `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are connected to the NexoraX NexoraX Tunnel. I am the ${editName}. State your authorization reference or corporate task.` }
      ]);
      
      // Auto plays initial greeting after brief simulation lag to match out-of-this-universe standards
      setTimeout(async () => {
        setPhoneState('active');
        try {
          const res = await fetch('/api/generate-voice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              text: `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are connected directly to the NexoraX NexoraX Voice system. I am the ${editName}. State your authorization reference or corporate task. All parameters are secured and tracked.`, 
              voiceName: editVoice 
            })
          });
          const data = await res.json();
          if (data.status === 'success' && data.audio) {
            const wavUrl = pcmBase64ToWavDataUrl(data.audio, 24000);
            setAudioUrl(wavUrl);
            const audio = new Audio(wavUrl);
            audio.play().catch(() => {});
          } else {
            throw new Error("TTS endpoint fallback");
          }
        } catch (err) {
          console.warn("Dial-in initial speech fallback triggered:", err);
          if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(`In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. You are connected directly to the NexoraX NexoraX Voice system. I am the ${editName}. State your authorization reference or corporate task. All parameters are secured and tracked.`);
            utterance.rate = 1.0;
            const voices = window.speechSynthesis.getVoices();
            let voiceObj = voices.find(v => {
              const n = v.name.toLowerCase();
              return (n.includes("female") || n.includes("zira") || n.includes("samantha") || n.includes("karen") || n.includes("hazel")) && v.lang.startsWith("en-");
            });
            if (!voiceObj) {
              voiceObj = voices.find(v => v.lang.startsWith("en-GB") || v.lang.startsWith("en-US") || v.name.toLowerCase().includes("female")) || voices[0];
            }
            if (voiceObj) utterance.voice = voiceObj;
            window.speechSynthesis.speak(utterance);
          }
        }
      }, 1500);

    } else {
      // Disconnect
      setPhoneState('idle');
      setCallSessionHistory([]);
      setSimulatedPromptInput('');
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    }
  };

  // Dispatch live dialogue signal to generate response + play back TTS live
  const handleSendClientDialogue = async () => {
    if (!simulatedPromptInput.trim() || isWaitingForTTS) return;
    
    const clientMsg = simulatedPromptInput.trim();
    setSimulatedPromptInput('');
    setCallSessionHistory(prev => [...prev, { role: 'client', text: clientMsg }]);
    setIsWaitingForTTS(true);

    let agentResponseText = `Acknowledged. I am processing your corporate directive for Lead Director under NexoraX billing records compliance lines. State further variables.`;
    try {
      // 1. Send conversation and the active specialist's system instructions to the AI chat API
      const historyParagraphs = callSessionHistory.map(msg => `${msg.role === 'client' ? 'User' : 'Assistant'}: ${msg.text}`);
      
      // We instruct the chat API of our active specialist instructions
      const chatHookRes = await fetch("/api/voice/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: `[SYSTEM CONTEXT: You are of the role '${editName}'. Always act under these exact studio rules: ${editPrompt}. State your answer concisely within 2 sentences for audio playback.] Client says: ${clientMsg}`,
          history: historyParagraphs
        })
      });

      const chatData = await chatHookRes.json();
      if (chatData.status === 'error') {
        throw new Error(chatData.message);
      }

      agentResponseText = chatData.reply || chatData.response || chatData.text || agentResponseText;

      setCallSessionHistory(prev => [...prev, { role: 'specialist', text: agentResponseText }]);

      // 2. Synthesize agent response text directly to audio via /api/generate-voice !
      const ttsRes = await fetch('/api/generate-voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: agentResponseText, 
          voiceName: editVoice 
        })
      });

      const ttsData = await ttsRes.json();
      if (ttsData.status === 'success' && ttsData.audio) {
        let rate = 24000;
        if (ttsData.mimeType) {
          const match = ttsData.mimeType.match(/rate=(\d+)/);
          if (match) rate = parseInt(match[1], 10);
        }
        const wavUrl = pcmBase64ToWavDataUrl(ttsData.audio, rate);
        setAudioUrl(wavUrl);
        
        // Output audio live!
        const audio = new Audio(wavUrl);
        audio.play().catch(e => {
          if (e && e.name === 'AbortError') {
            console.log("Dial-in audio playback interrupted gracefully.");
          } else {
            console.log("Dial-in play bypassed:", e?.message);
          }
        });
        
        setSuccessLogs(prev => [
          `[TELECOM/DIALIN] Received audio stream response for caller dialogue.`,
          `[TELECOM/METRIC] Packet turnaround complied with 0.8s standard.`,
          ...prev.slice(0, 8)
        ]);
      } else {
        throw new Error(ttsData.message || "Failed to generate TTS audio.");
      }

    } catch (err: any) {
      console.warn("Real dial-in Speech synthesis fell back. Employing fallback SpeechSynthesis:", err);
      const fallbackResponse = agentResponseText || `Understood. Proceed to configure our Stripe Payment link (https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00) or check routing.`;
      
      if (!callSessionHistory.find(h => h.text === fallbackResponse)) {
        setCallSessionHistory(prev => [...prev, { role: 'specialist', text: fallbackResponse }]);
      }
      
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(fallbackResponse);
        utterance.rate = 1.0;
        const voices = window.speechSynthesis.getVoices();
        let voiceObj = voices.find(v => {
          const n = v.name.toLowerCase();
          return (n.includes("female") || n.includes("zira") || n.includes("samantha") || n.includes("karen") || n.includes("hazel")) && v.lang.startsWith("en-");
        });
        if (!voiceObj) {
          voiceObj = voices.find(v => v.lang.startsWith("en-GB") || v.lang.startsWith("en-US") || v.name.toLowerCase().includes("female")) || voices[0];
        }
        if (voiceObj) utterance.voice = voiceObj;
        window.speechSynthesis.speak(utterance);
      }
    } finally {
      setIsWaitingForTTS(false);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto pt-2 relative z-10 font-sans p-2 sm:p-4 flex flex-col space-y-6 select-text">
      
      {/* HEADER SECTION WITH DEPLOYED STATUS */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between border-b border-cyan-500/10 pb-5 gap-4">
        <div>
          <div className="text-cyan-400 font-bold text-xs uppercase tracking-[0.4em] flex items-center gap-2">
            <Cpu className="w-4 h-4 animate-spin text-cyan-400" style={{ animationDuration: '4s' }} />
            <span>NEXORAX NEXORAX TELEPHONY CORE</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase mt-1">
            NODE-66.2 VOICE MATRIX
          </h2>
          <p className="text-slate-400 font-mono text-[11px] uppercase tracking-wider mt-1 max-w-3xl leading-relaxed">
            Holographic voice matrix of 11 prebuilt deployable AI voice assistants, ready for customization. Clients can create, style, and launch bespoke virtual assistants automatically configured for whatever system operations, customer support, or inbound/outbound telecommunication needs they possess.
          </p>
        </div>

        {/* Global Telecom Stat Deck */}
        <div className="flex flex-wrap gap-2 sm:gap-3 bg-slate-950 p-3 rounded-xl border border-cyan-500/10 font-mono text-[9px] uppercase tracking-wider text-slate-400 w-full lg:w-auto">
          <div className="flex flex-col pr-3 border-r border-slate-900">
            <span className="text-[8px] text-cyan-500/60 font-bold">LATENCY</span>
            <span className="text-emerald-400 font-black mt-0.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {latencyMetric}S SLA
            </span>
          </div>
          <div className="flex flex-col pr-3 border-r border-slate-900">
            <span className="text-[8px] text-cyan-500/60 font-bold">TUNNEL SECURE</span>
            <span className="text-cyan-400 font-black mt-0.5">08000 622827</span>
          </div>
          <div className="flex flex-col pr-3 border-r border-slate-900">
            <span className="text-[8px] text-cyan-500/60 font-bold">LEDGER TARGETS</span>
            <span className="text-amber-400 font-bold mt-0.5">11 ACTIVE NODES</span>
          </div>
          <div className="flex flex-col pr-3">
            <span className="text-[8px] text-cyan-500/60 font-bold">SECURE DRIVE</span>
            <span className="text-slate-200 mt-0.5 font-bold">{activeHandshakeCode}</span>
          </div>
          <button 
            onClick={handleResetToDefaults}
            className="p-1 px-2.5 bg-slate-900 border border-slate-800 hover:border-cyan-500/30 hover:text-white rounded-lg transition-colors flex items-center gap-1 text-[8px] font-mono text-slate-500 uppercase self-center"
            title="Reset Specialists to Factory Defaults"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>

      {/* OUT-OF-THIS-UNIVERSE CLIENT-DRIVEN SELF-SERVE AI ASSISTANTS CTA BANNER */}
      <div id="self-serve-cta-banner" className="bg-gradient-to-r from-slate-950 via-[#01091a] to-black border-2 border-cyan-500/30 rounded-2xl p-6 relative overflow-hidden shadow-[0_0_25px_rgba(6,182,212,0.15)] flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="absolute top-0 right-0 w-80 h-full bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="flex items-start gap-4">
          <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl text-cyan-400 shrink-0">
            <Sparkles className="w-5.5 h-5.5 animate-pulse" />
          </div>
          <div>
            <div className="text-cyan-400 font-mono text-[9px] uppercase font-black tracking-widest flex items-center gap-1.5">
              <span>MEMBER SPECIALIST PROVISIONER</span>
              <span className="px-1.5 py-0.5 bg-cyan-400/10 text-cyan-400 rounded text-[7px] font-bold">SELF-SERVE</span>
            </div>
            <h3 className="text-lg md:text-xl font-sans font-black text-white tracking-tight uppercase mt-1">
              BUILD YOUR CUSTOM AI VOICE ASSISTANT IN SECONDS
            </h3>
            <p className="text-[11px] text-slate-400 mt-1 max-w-2xl font-mono leading-relaxed uppercase">
              All prebuilt units function as editable formats. You can instantly create a bespoke AI Assistant tailored specifically for your own website, appointment schedules, client feedback loops, outbound campaigns, or coordinate support workflows!
            </p>
          </div>
        </div>

        {/* PROMINENT CALL TO ACTION TO CREATE ASSISTANT */}
        <button
          id="btn-create-assistant-cta"
          onClick={() => setShowCreateModal(true)}
          className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-extrabold uppercase tracking-widest text-xs rounded-xl shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.7)] hover:from-emerald-400 hover:to-cyan-400 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 stroke-[3px]" />
          <span>CREATE AI ASSISTANT NOW</span>
        </button>
      </div>

      {/* 11 SPEC DIRECTORY SELECTOR */}
      <div className="bg-[#02050b] border border-slate-800/80 rounded-2xl p-4 shadow-2xl relative">
        <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-3 flex items-center justify-between">
          <span>SELECT SPECIALIST MATRIX ROLE</span>
          <span className="text-cyan-400 font-semibold normal-case font-sans italic flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Unified Control Interface
          </span>
        </h3>
        
        {/* Horizontal scroll grid of the 11 business units */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2 overflow-x-auto pb-1 custom-scrollbar">
          {specialists.map((spec) => {
            const isSelected = spec.id === selectedSpecialistId;
            return (
              <button
                key={spec.id}
                onClick={() => setSelectedSpecialistId(spec.id)}
                className={`p-2 rounded-xl text-left border relative transition-all duration-300 min-h-[58px] flex flex-col justify-between ${
                  isSelected 
                    ? 'bg-cyan-500/10 border-cyan-400 text-white shadow-[0_0_15px_rgba(6,182,212,0.12)]' 
                    : 'bg-black/40 border-slate-900 text-slate-400 hover:border-slate-800 hover:bg-slate-950 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="bg-slate-900 border border-slate-800 text-cyan-400 text-[6px] px-1 rounded-sm font-mono tracking-wider font-extrabold block truncate scale-95 origin-left">
                    {spec.roleKey.toUpperCase()}
                  </span>
                  {isSelected && <span className="w-1 h-1 rounded-full bg-cyan-400 animate-ping" />}
                </div>
                <div className="text-[10px] uppercase font-bold tracking-tight line-clamp-2 truncate mt-1">
                  {spec.name}
                </div>
              </button>
            );
          })}

          {/* Add Specialist Trigger Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-2 bg-cyan-500/5 hover:bg-cyan-500/10 text-cyan-400 rounded-xl border border-dashed border-cyan-500/30 flex flex-col items-center justify-center gap-1 transition-all min-h-[58px]"
          >
            <Plus className="w-4 h-4" />
            <span className="text-[8px] font-mono uppercase tracking-widest font-black">DEPLOY NEW</span>
          </button>
        </div>
      </div>

      {/* THREE INTERACTIVE MASTER WORKSPACES COLUMNS */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        
        {/* LEFT PANEL (7/12 Width) - The Specialist Studio Customizer & Prompt Tuning */}
        <div className="xl:col-span-7 flex flex-col space-y-6">
          
          <div className="bg-[#030612] border border-slate-800/80 rounded-2xl p-5 shadow-2xl relative overflow-hidden flex flex-col justify-between h-full min-h-[580px]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
            
            {/* Header control with delete options for custom bots */}
            <div>
              <div className="flex items-start justify-between border-b border-slate-900 pb-3 mb-4">
                <div>
                  <div className="text-[9px] text-slate-500 font-mono uppercase tracking-widest font-bold">SPECIALIST METRIC DESIGNER</div>
                  <h4 className="text-white text-xl font-sans font-black uppercase mt-1 flex items-center gap-2">
                    {activeSpecialist.isCustom ? <Zap className="w-4.5 h-4.5 text-cyan-400" /> : <Shield className="w-4.5 h-4.5 text-cyan-400" />}
                    <span>{editName}</span>
                  </h4>
                </div>
                
                {activeSpecialist.isCustom && (
                  <button
                    onClick={() => handleDeleteSpecialist(activeSpecialist.id, activeSpecialist.name)}
                    className="p-2 border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-black rounded-xl transition-all duration-200 text-xs font-mono uppercase flex items-center gap-1.5"
                  >
                    <Trash2 className="w-4 h-4" /> Drop Node
                  </button>
                )}
              </div>

              {/* TWO COLUMN PARAMETER TUNERS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Core Settings Block */}
                <div className="space-y-4">
                  
                  {/* Name field */}
                  <div className="flex flex-col">
                    <label className="text-[9px] text-slate-400 font-mono uppercase font-black tracking-widest mb-1.5">Specialist Bot Name</label>
                    <input 
                      type="text"
                      className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-slate-200 text-xs font-mono uppercase focus:outline-none focus:border-cyan-400 transition"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>

                  {/* Duty Description */}
                  <div className="flex flex-col">
                    <label className="text-[9px] text-slate-400 font-mono uppercase font-black tracking-widest mb-1.5">Dynamic Duty Description</label>
                    <input 
                      type="text"
                      className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-slate-200 text-xs focus:outline-none focus:border-cyan-400 transition"
                      value={editDuty}
                      onChange={(e) => setEditDuty(e.target.value)}
                    />
                  </div>

                  {/* Vocal Accent / Voice Selector */}
                  <div className="flex flex-col">
                    <label className="text-[9px] text-slate-400 font-mono uppercase font-black tracking-widest mb-1.5">Default Phonetic Acoustic</label>
                    <select 
                      className="bg-black/60 border border-slate-800 rounded-lg p-2.5 text-slate-200 text-xs font-mono focus:outline-none focus:border-cyan-400 transition"
                      value={editVoice}
                      onChange={(e) => setEditVoice(e.target.value)}
                    >
                      {ACOUSTIC_VOICES.map(v => (
                        <option key={v.id} value={v.id}>{v.name} ({v.frequency})</option>
                      ))}
                    </select>
                  </div>

                </div>

                {/* Simulated Dial-in Parameters & Acoustic Settings */}
                <div className="space-y-3.5 bg-[#010408] border border-slate-900 rounded-xl p-4">
                  <div className="text-[9px] text-cyan-400 font-mono uppercase tracking-widest font-black flex items-center gap-1.5 pb-2 border-b border-slate-950">
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    <span>Fine Tuning Controls</span>
                  </div>

                  {/* Posture */}
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-[9px] text-slate-500 uppercase font-black">Emotional Attitude</span>
                    <select
                      className="bg-black border border-slate-800 rounded text-slate-300 text-[10px] p-1 font-mono uppercase focus:outline-none focus:border-cyan-400"
                      value={editPosture}
                      onChange={(e: any) => setEditPosture(e.target.value)}
                    >
                      <option value="authoritative">Authoritative</option>
                      <option value="calm">Calm</option>
                      <option value="playful">Playful (Deal-Closer)</option>
                      <option value="intense">Intense (Debt Rescue)</option>
                      <option value="assertive">Assertive</option>
                    </select>
                  </div>

                  {/* Telephony SIP Route */}
                  <div className="flex items-center justify-between text-xs font-mono pt-1">
                    <span className="text-[9px] text-slate-500 uppercase font-black">Dial-In SIP Routing (0800 Status)</span>
                    <label className="relative inline-flex items-center cursor-pointer select-none">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={editSipRouting}
                        onChange={(e) => setEditSipRouting(e.target.checked)}
                      />
                      <div className="w-9 h-5 bg-slate-900 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 peer-checked:after:bg-cyan-400 after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500/20 border border-slate-800" />
                    </label>
                  </div>

                  {/* Simulated Metrics for the Bot Grid */}
                  <div className="pt-2 border-t border-slate-950 text-[10px] font-mono space-y-1.5 text-slate-400">
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] text-slate-500 uppercase">Target SLA Latency</span>
                      <span className="text-slate-300 font-bold">{editVoice === 'Fenrir' ? '0.4s' : editVoice === 'Charon' ? '0.5s' : '0.8s'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] text-slate-500 uppercase">Interactive Accuracy Metric</span>
                      <span className="text-slate-300 font-bold">{activeSpecialist.accuracyRateSim}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] text-slate-500 uppercase">Task Conversion Index</span>
                      <span className="text-slate-300 font-bold">{activeSpecialist.conversionRateSim}%</span>
                    </div>
                  </div>

                </div>

              </div>
            </div>

            {/* LIVE EDITABLE SYSTEM PROMPT WITH SYNTAX CONTAINER */}
            <div className="mt-4 flex flex-col space-y-2">
              <div className="flex items-center justify-between font-mono text-[9px]">
                <span className="text-slate-400 font-black tracking-widest uppercase">NEXORAX SYSTEM PROMPT (LIVE COMPILABLE)</span>
                <span className="text-slate-500 uppercase">{editPrompt.length} / 1200 MAX CHARS</span>
              </div>
              <textarea
                className="w-full bg-black/60 border border-slate-800 rounded-xl p-3 text-slate-200 font-mono text-xs focus:outline-none focus:border-cyan-400 transition custom-scrollbar uppercase tracking-wider leading-relaxed"
                rows={5}
                maxLength={1200}
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder="DEFINE THE SYSTEM INSTRUCTION PATTERNS..."
              />

              {/* Nexorax AI Prompt Optimizer Area */}
              <div className="p-4 bg-cyan-950/10 border border-cyan-500/10 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-400/5 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-start gap-2.5">
                  <div className="bg-cyan-500/10 p-2 rounded-lg border border-cyan-500/20 shrink-0 text-cyan-400 mt-0.5">
                    <Sparkles className="w-4 h-4 animate-pulse" />
                  </div>
                  <div>
                    <div className="text-[10px] text-cyan-400 font-mono font-black uppercase tracking-wider">
                      Neural Prompt Optimizer AI Core
                    </div>
                    <p className="text-[9px] text-slate-400 mt-1 uppercase max-w-md">
                      Triggers real-time out-of-this-universe alignment models via Gemini API resources. Integrates the 1% Levy, 20% Tax, and direct support lines seamlessly.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={enhancePromptWithAI}
                  disabled={isOptimizingPrompt || !editPrompt.trim()}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase text-[10px] tracking-wider py-2 px-4 rounded-lg flex items-center gap-1.5 transition-all w-full sm:w-auto justify-center disabled:opacity-40"
                >
                  {isOptimizingPrompt ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> OVERHAULING INSTRUCTIONS...</>
                  ) : (
                    <><Zap className="w-3.5 h-3.5 fill-black" /> ENHANCE VIA COGNITION</>
                  )}
                </button>
              </div>

            </div>

            {/* ACTION FOOTER CONTROLS */}
            <div className="mt-4 pt-3 border-t border-slate-900/80 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-[8px] text-cyan-500/50 font-mono uppercase tracking-widest leading-none">
                MANDATORY ESCROW STANDARDS: TAX extractions interlocked
              </span>
              
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {/* Generate Voice Payload test */}
                <button
                  onClick={generateVoice}
                  disabled={isGenerating || !editPrompt.trim()}
                  className="flex-1 sm:flex-none border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/10 text-cyan-400 font-mono font-bold text-xs uppercase px-4 py-2.5 rounded-xl transition flex items-center justify-center gap-1.5"
                  title="Generate WAV output for current System Prompt configuration"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> COMPILING...</>
                  ) : (
                    <><Radio className="w-3.5 h-3.5 text-cyan-400" /> TEST ACOUSTIC COMPILER</>
                  )}
                </button>

                {/* Save Node configuration */}
                <button
                  onClick={handleSaveActiveSpecialist}
                  className="flex-1 sm:flex-none bg-cyan-500 hover:bg-cyan-400 text-black font-mono font-black text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition font-black flex items-center justify-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" /> SAVE BOT SCHEMA
                </button>
              </div>
            </div>

          </div>

          {/* Quick Policy Directives suggestions block */}
          <div className="bg-[#030612] border border-slate-800/80 rounded-2xl p-5 shadow-2xl">
            <div className="flex items-start justify-between mb-3.5">
              <div>
                <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold">
                  NexoraX Suggestion Directive Library
                </h3>
                <p className="text-[10px] text-slate-500 font-mono uppercase mt-0.5">
                  1-Click policy rules injection. Insert strict compliance tags directly to active memory.
                </p>
              </div>
              <span className="bg-amber-400/10 text-amber-500 text-[8px] font-mono px-2 py-0.5 rounded font-black uppercase">
                Studio Assets
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {INJECTION_TEMPLATES.map((tmpl, idx) => (
                <button
                  key={idx}
                  onClick={() => handleInjectTemplate(tmpl.text)}
                  className="p-2.5 bg-black border border-slate-900 rounded-xl hover:border-slate-800 hover:bg-slate-950 transition-colors text-left group flex flex-col justify-between"
                  title={`Click to append directive parameters for ${tmpl.name}`}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-[14px]">{tmpl.icon}</span>
                    <span className="text-[7px] text-slate-500 font-mono font-bold uppercase tracking-wider">
                      {tmpl.category}
                    </span>
                  </div>
                  <div className="text-[9px] font-sans font-black text-cyan-400 group-hover:text-cyan-300 mt-2 uppercase tracking-tight truncate w-full">
                    {tmpl.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN (5/12 Width) - Real-time Audio DSP & Dial-in Telephone Call Simulator */}
        <div className="xl:col-span-5 flex flex-col space-y-6">
          
          {/* THE WEB AUDIO ANALYZER & DSP RACK */}
          <div className="bg-[#030612] border border-slate-800/80 rounded-2xl p-5 shadow-2xl flex flex-col">
            <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center justify-between">
              <span>[04] ANALYZER ACOUSTICS WORKBENCH</span>
              <Activity className="w-4.5 h-4.5 text-cyan-400" />
            </h3>

            {/* SPECTRUM CANVAS */}
            <div className="w-full h-40 rounded-xl border border-slate-900 overflow-hidden bg-[#010408] relative flex flex-col items-center justify-center">
              <canvas 
                ref={canvasRef} 
                className="absolute inset-0 w-full h-full" 
                width={380} 
                height={160} 
              />
              
              {!audioUrl && !isGenerating && (
                <div className="relative z-10 flex flex-col items-center justify-center text-slate-500 text-center font-mono p-4">
                  <Radio className="w-8 h-8 text-slate-800 animate-pulse mb-1.5" />
                  <span className="text-[9px] uppercase tracking-widest text-slate-400">SPECTRUM BUFFER VACANT</span>
                  <span className="text-[7px] text-slate-600 mt-1 uppercase max-w-xs leading-normal">
                    synthesize voice above or trigger simulated call dialog to activate voice spectrogram
                  </span>
                </div>
              )}

              {isGenerating && (
                <div className="relative z-10 bg-black/80 border border-slate-800/80 p-4 rounded-xl flex flex-col items-center text-center font-mono">
                  <Loader2 className="w-7 h-7 animate-spin text-cyan-500 mb-1.5" />
                  <span className="text-cyan-500 text-[9px] font-black uppercase tracking-[0.2em]">GENERATING FREQUENCY MAPS</span>
                </div>
              )}
            </div>

            {/* DSP RACK PLUG-INS */}
            <div className="mt-4 space-y-3.5">
              <div>
                <span className="text-[8px] text-slate-500 font-mono font-black uppercase tracking-wider block mb-1.5">
                  DSP ACCENT PROFILE COMPRESSION
                </span>
                <div className="grid grid-cols-5 gap-1">
                  {(['dry', 'telephony', 'vault', 'sub_bass', 'high_pass'] as const).map((effect) => (
                    <button
                      key={effect}
                      onClick={() => setDspEffect(effect)}
                      disabled={!audioUrl}
                      className={`py-1.5 px-0.5 text-center rounded text-[8px] font-mono uppercase tracking-wider transition border ${
                        !audioUrl 
                          ? 'opacity-40 cursor-not-allowed border-slate-950 bg-black/10 text-slate-600'
                          : dspEffect === effect
                            ? 'bg-cyan-500/15 border-cyan-400 text-cyan-400 font-bold'
                            : 'bg-black border-slate-900 text-slate-500 hover:border-slate-800 hover:text-slate-300'
                      }`}
                    >
                      {effect.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* TIMBRE & SLIDER GAINS */}
              <div className="grid grid-cols-2 gap-3.5 pt-1">
                <div className="bg-black/40 p-2.5 rounded-xl border border-slate-900 font-mono text-[9px] text-slate-400 flex flex-col space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="uppercase text-[8px] text-slate-500 font-bold">PLAY-SPEED</span>
                    <span className="text-cyan-400 font-bold">{playSpeed.toFixed(1)}x</span>
                  </div>
                  <input 
                    type="range" 
                    min="0.5" 
                    max="2.0" 
                    step="0.1"
                    disabled={!audioUrl}
                    className="w-full accent-cyan-500 cursor-pointer disabled:opacity-40"
                    value={playSpeed}
                    onChange={(e) => {
                      const speed = parseFloat(e.target.value);
                      setPlaySpeed(speed);
                      if (audioRef.current) {
                        audioRef.current.playbackRate = speed;
                      }
                    }}
                  />
                </div>

                <div className="bg-black/40 p-2.5 rounded-xl border border-slate-900 font-mono text-[9px] text-slate-400 flex flex-col space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="uppercase text-[8px] text-slate-500 font-bold">SONIC GAIN</span>
                    <span className="text-cyan-400 font-bold">{amplifier.toFixed(1)}x</span>
                  </div>
                  <input 
                    type="range" 
                    min="0.4" 
                    max="2.5" 
                    step="0.1"
                    disabled={!audioUrl}
                    className="w-full accent-cyan-500 cursor-pointer disabled:opacity-40"
                    value={amplifier}
                    onChange={(e) => setAmplifier(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* CURRENT CACHED SOURCE LOADER */}
            <div className="mt-4 pt-3.5 border-t border-slate-950 bg-slate-950/45 p-3.5 rounded-xl flex items-center justify-between">
              {audioUrl ? (
                <div className="flex items-center gap-2.5 w-full">
                  <button
                    onClick={triggerAudioPlayback}
                    className="w-10 h-10 bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center rounded-full shadow-[0_0_12px_rgba(6,182,212,0.25)] transition shrink-0"
                  >
                    {isPlaying ? <Pause className="w-4.5 h-4.5 fill-black" /> : <Play className="w-4.5 h-4.5 fill-black ml-0.5" />}
                  </button>

                  <div className="flex-1 min-w-0 font-mono text-[8px] text-slate-400">
                    <div className="text-white text-[9px] font-bold uppercase truncate flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                      <span className="truncate">{editVoice}_MATRIX_AUDIT.WAV</span>
                    </div>
                    <div className="uppercase text-[7px] text-slate-500 mt-0.5">
                      PCM | {sampleRate} HZ | LATENCY OUT-OF-BAND SECURE
                    </div>
                  </div>

                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    className="hidden"
                    onCanPlay={() => setupAudioGraph()}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                  />

                  <button
                    onClick={downloadAudioFile}
                    className="p-2 border border-slate-800 bg-slate-900 hover:text-white text-slate-400 rounded-lg transition shrink-0"
                    title="Download WAV File"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full py-2.5 text-center text-slate-600 font-mono text-[8px] uppercase gap-1.5">
                  <VolumeX className="w-4 h-4 text-slate-800" />
                  <span>TTS Cache Sandbox Empty</span>
                </div>
              )}
            </div>

          </div>

          {/* HOLOGRAPHIC PHONE DIAL-IN SIMULATOR (OUT-OF-THIS-UNIVERSE PHONE OVERLAY) */}
          <div className="bg-[#030612] border border-cyan-500/10 rounded-2xl p-5 shadow-2xl flex-1 flex flex-col justify-between min-h-[380px] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-cyan-950/5 pointer-events-none" />
            
            <div>
              <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4">
                <div>
                  <h4 className="text-white text-xs font-mono font-black uppercase tracking-[0.2em] flex items-center gap-1.5">
                    <PhoneCall className="w-4 h-4 text-emerald-400" />
                    <span>DIAL-IN VOCAL WORKSTATION</span>
                  </h4>
                  <p className="text-[9px] text-slate-500 font-mono mt-0.5 uppercase">
                    Test the active specialist via real simulated vocal dialogue tunnels.
                  </p>
                </div>

                <button
                  onClick={handleInitiatePhoneCall}
                  className={`px-3 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-widest font-bold transition-all flex items-center gap-1.5 ${
                    phoneState === 'active'
                      ? 'bg-rose-500/15 border border-rose-500/40 text-rose-400'
                      : phoneState === 'connecting'
                        ? 'bg-amber-500/15 border border-amber-500/40 text-amber-400 animate-pulse'
                        : 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-black hover:border-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.1)]'
                  }`}
                >
                  {phoneState === 'active' ? (
                    <><PhoneOff className="w-3.5 h-3.5 text-rose-400" /> DISCONNECT</>
                  ) : phoneState === 'connecting' ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> CONNECTING...</>
                  ) : (
                    <><PhoneCall className="w-3.5 h-3.5" /> DIAL 0800</>
                  )}
                </button>
              </div>

              {/* LIVE PHONE SCREEN WORKSPACE */}
              <div className="bg-[#020409] border border-slate-900/80 rounded-xl p-4 h-56 overflow-y-auto custom-scrollbar flex flex-col space-y-3">
                {phoneState === 'idle' ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-slate-600 font-mono space-y-2 select-none uppercase">
                    <Mic className="w-10 h-10 text-slate-800 animate-pulse" />
                    <div className="text-[10px] text-slate-500 font-black tracking-widest">VOICE TUNNEL OFFLINE</div>
                    <div className="text-[8px] text-slate-700 max-w-sm ml-1.5">
                      dial the secure Planet Telecom 0800 frequency above to instantiate vocal handshake coordinates.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center font-mono text-[7px] text-cyan-500/60 uppercase racking-widest py-1 border-b border-slate-950 flex items-center justify-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-emerald-500 animate-ping" />
                      SECURE DIGITAL SIP TUNNEL INITIATED - CALL RECORDING ACTIVE
                    </div>
                    {callSessionHistory.map((msg, index) => {
                      const isAgent = msg.role === 'specialist';
                      return (
                        <div 
                          key={index} 
                          className={`flex gap-2 ${isAgent ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className={`p-3 rounded-xl max-w-[85%] font-mono text-[10px] leading-relaxed select-text ${
                            isAgent 
                              ? 'bg-cyan-950/10 border border-cyan-500/10 text-cyan-300' 
                              : 'bg-slate-900 text-slate-100 border border-slate-800'
                          }`}>
                            <div className="text-[7px] text-slate-500 font-extrabold uppercase tracking-widest mb-1">
                              {isAgent ? editName : 'INBOUND CALLER'}
                            </div>
                            <div className="uppercase tracking-wide">{msg.text}</div>
                          </div>
                        </div>
                      );
                    })}
                    {isWaitingForTTS && (
                      <div className="flex justify-start">
                        <div className="bg-black border border-slate-900 rounded-xl p-3 flex items-center gap-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
                          <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest animate-pulse">Specialist is speaking...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* SEND CALLER PROMPT BOX */}
            {phoneState === 'active' && (
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="text"
                  className="flex-1 bg-black border border-slate-800 rounded-xl px-4 py-3 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-400 uppercase tracking-widest"
                  placeholder="Incept caller dialogue (e.g., 'Do you use Secure Payment Provider?')..."
                  value={simulatedPromptInput}
                  onChange={(e) => setSimulatedPromptInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSendClientDialogue();
                  }}
                  disabled={isWaitingForTTS}
                />
                <button
                  onClick={handleSendClientDialogue}
                  disabled={isWaitingForTTS || !simulatedPromptInput.trim()}
                  className="bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 p-3 rounded-xl text-black transition shrink-0 flex items-center justify-center"
                >
                  <Send className="w-4 h-4 fill-black" />
                </button>
              </div>
            )}

            {/* Secure status metadata */}
            <div className="mt-4 pt-3 border-t border-slate-900/80 flex items-center justify-between text-[8px] font-mono uppercase text-slate-500">
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-amber-500" />
                <span>eIDAS Authenticated Biometric Link</span>
              </div>
              <span>REGISTRY ID: UK-NEX-0199</span>
            </div>

          </div>

          {/* DOCK TRANSACTIONAL TIMELINE LOGS */}
          <div className="bg-[#030612] border border-slate-800/80 rounded-2xl p-5 shadow-2xl flex flex-col justify-between">
            <div>
              <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-1.5">
                <Compass className="w-4.5 h-4.5 text-amber-500" />
                <span>NEXORAX CORES SYSTEM TELEMETRY INDEX</span>
              </h3>
              
              <div className="space-y-1.5 max-h-[148px] overflow-y-auto custom-scrollbar font-mono text-[8px] text-slate-400 bg-black/60 p-3 rounded-xl border border-slate-950">
                {successLogs.map((log, index) => {
                  let isError = log.includes('ERROR') || log.includes('Failed') || log.includes('FALLBACK');
                  let isStatus = log.includes('STATUS') || log.includes('TELECOM') || log.includes('STUDIO/');
                  return (
                    <div 
                      key={index} 
                      className={`border-b border-slate-950/40 pb-1 leading-normal uppercase select-none ${
                        isError ? 'text-rose-500 font-bold' : isStatus ? 'text-cyan-400' : 'text-slate-300'
                      }`}
                    >
                      <span className="text-slate-600 mr-1 opacity-70">[{index + 1}]</span>
                      {log}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-[7px] font-mono uppercase text-slate-500 select-none">
              <div className="flex flex-col">
                <span className="font-bold text-slate-400">PATENT COHESION REFERENCE</span>
                <span>UNITED KINGDOM PATENT FI2026-NEX</span>
              </div>
              <div className="text-right flex flex-col">
                <span className="font-bold text-slate-400">Escrow Auditing</span>
                <span>1% Perpetual Levy / 10% Maintenance & Hosting Fee</span>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* ====== [NODE-66.3] AI ASSISTANT SQUADS COMMAND CENTER ====== */}
      <div id="ai-squads-commander" className="bg-[#02050e] border border-cyan-500/10 rounded-2xl p-5 shadow-2xl relative overflow-hidden flex flex-col space-y-6">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-slate-900 pb-4 gap-2">
          <div>
            <div className="text-emerald-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
              <Users className="w-4 h-4 text-emerald-400" />
              <span>NODE-66.3 SQUAD MATRIX PROVISIONER</span>
            </div>
            <h3 className="text-xl md:text-2xl font-sans font-black text-white tracking-tight uppercase mt-1">
              AI ASSISTANT SQUAD CO-OPERATIVE DIRECTORY
            </h3>
            <p className="text-slate-400 font-mono text-[10px] uppercase mt-1.5 max-w-4xl leading-relaxed">
              Interlink multiple highly-trained virtual specialists into coherent multi-agent taskforces. Squads work concurrently to satisfy multi-stage NexoraX workflows, from automatic vetting and Client Resource Workflow strikes to ledger audit extractions and Secure Payment Provider compliance checks.
            </p>
          </div>

          {/* Primary action to trigger create squad */}
          <button
            onClick={() => setShowCreateSquadModal(true)}
            className="px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500 hover:text-black hover:border-emerald-500 text-emerald-400 text-xs font-mono font-black uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.05)] self-end md:self-center"
          >
            <Plus className="w-4 h-4" />
            <span>PROVISION NEW SQUAD</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          
          {/* ROSTER PANEL (Col-span 5) */}
          <div className="lg:col-span-5 flex flex-col space-y-4 font-mono">
            <div className="text-[9px] text-slate-500 uppercase tracking-widest font-black flex items-center justify-between">
              <span>ACTIVE SQUAD REGISTRY</span>
              <span>{squads.length} NODES MOUNTED</span>
            </div>

            <div className="space-y-3 max-h-[390px] overflow-y-auto custom-scrollbar pr-1">
              {squads.map((sq) => {
                const isSelected = sq.id === selectedSquadId;
                const isOrchestrating = sq.id === activeOrchestrationSquadId && isSimulatingSquad;
                
                return (
                  <div
                    key={sq.id}
                    onClick={() => !isSimulatingSquad && setSelectedSquadId(sq.id)}
                    className={`p-4 rounded-xl border transition-all duration-300 relative ${
                      isSimulatingSquad ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${
                      isSelected 
                        ? 'bg-emerald-950/10 border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.1)] text-white' 
                        : 'bg-black/35 border-slate-900 text-slate-400 hover:border-slate-800 hover:text-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="bg-slate-900 border border-slate-800 px-1.5 py-0.5 text-emerald-400 rounded text-[7px] font-mono tracking-widest uppercase font-extrabold flex items-center gap-1">
                            <Workflow className="w-3.5 h-3.5" />
                            {sq.isCustom ? "CUSTOM_TASKFORCE" : "PREBUILT"}
                          </span>
                          {sq.status === 'SUCCESS' && (
                            <span className="text-emerald-400 text-[7px] font-mono font-bold flex items-center gap-0.5 bg-emerald-500/10 border border-emerald-500/20 px-1 rounded">
                              <CheckCircle2 className="w-2.5 h-2.5" /> SECURED
                            </span>
                          )}
                          {isOrchestrating && (
                            <span className="text-amber-400 text-[7px] font-mono font-bold flex items-center gap-0.5 bg-amber-500/10 border border-amber-500/20 px-1 rounded animate-pulse">
                              <Loader2 className="w-2.5 h-2.5 animate-spin" /> RUNNING
                            </span>
                          )}
                        </div>
                        <h4 className={`text-xs font-sans font-extrabold uppercase tracking-tight mt-2 ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {sq.name}
                        </h4>
                      </div>

                      {sq.isCustom && !isSimulatingSquad && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSquad(sq.id, sq.name);
                          }}
                          className="p-1 hover:bg-rose-500/20 hover:text-rose-400 text-slate-500 rounded transition-colors cursor-pointer"
                          title="Decommit Custom Squad"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <p className="text-[10px] text-slate-400 mt-2 line-clamp-2 leading-relaxed uppercase">
                      {sq.description}
                    </p>

                    {/* Members nodes ticker */}
                    <div className="mt-3.5 pt-2.5 border-t border-slate-950/40 flex items-center justify-between text-[8px] font-mono">
                      <div className="flex items-center gap-1.5 bg-transparent">
                        <span className="text-slate-500 font-bold uppercase">NODES:</span>
                        <div className="flex gap-1">
                          {sq.specialistIds.map((id, index) => {
                            const specObj = specialists.find(s => s.id === id);
                            return (
                              <span 
                                key={id}
                                className="px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded-md text-[7px] text-slate-400 font-semibold"
                                title={specObj?.name || "Vetting Node"}
                              >
                                {specObj ? specObj.name.split(' ')[0] : `Node-${index}`}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <span className="text-slate-500">{sq.specialistIds.length} SPECIALISTS</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* MONITOR / TERMINAL PANEL (Col-span 7) */}
          <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
            {(() => {
              const selectedSquad = squads.find(s => s.id === selectedSquadId);
              if (!selectedSquad) return (
                <div className="bg-[#01040a] border border-slate-900 rounded-xl p-6 h-full flex flex-col items-center justify-center text-center font-mono">
                  <Radio className="w-8 h-8 text-slate-800 animate-pulse mb-2" />
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Select Active Squad Node</span>
                </div>
              );

              return (
                <div className="bg-[#01040a] border border-slate-900 rounded-xl p-5 flex flex-col justify-between h-full space-y-4 relative min-h-[440px]">
                  {/* Ambient secure overlay indicator */}
                  <div className="absolute top-2 right-3 font-mono text-[7px] text-emerald-500/50 uppercase tracking-widest flex items-center gap-1 select-none">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping inline-block" />
                    <span>COOPERATIVE ORCHESTRATOR LIVE Live v2.6</span>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="text-slate-500 font-mono text-[8px] uppercase tracking-wider font-extrabold">ACTIVE OPERATIONAL TASKFORCE INTEL</span>
                      <h4 className="text-white text-md font-sans font-black uppercase mt-1 tracking-tight">
                        {selectedSquad.name}
                      </h4>
                      <div className="bg-[#02050f] border border-emerald-500/10 p-3 rounded-lg mt-2 text-[10px] text-slate-400 font-mono tracking-tight uppercase leading-relaxed">
                        <div className="font-bold text-emerald-400 text-[8px] tracking-wider uppercase mb-1">TACTICAL COOPERATIVE OBJECTIVE:</div>
                        {selectedSquad.tacticalObjective}
                      </div>
                    </div>

                    {/* LIVE CONCURRENT TERMINAL MONITOR */}
                    <div className="flex flex-col space-y-3">
                      <span className="text-[8px] text-slate-500 font-mono font-black uppercase tracking-widest block">TELEMETRY DIALOG STREAM MONITOR</span>
                      
                      <div className="bg-black/90 border border-slate-950 rounded-xl p-4 h-64 overflow-y-auto custom-scrollbar space-y-3 font-mono text-[10px]">
                        {orchestrationLogs.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-center text-slate-600 uppercase select-none space-y-2">
                            <Cpu className="w-8 h-8 text-slate-800 animate-spin" style={{ animationDuration: '6s' }} />
                            <div className="text-[9px] text-slate-500 font-black">CHANNEL STATE: STANDBY</div>
                            <div className="text-[7.5px] text-slate-700 max-w-sm">
                              Initialize Taskforce simulation below to observe real-time co-operative cross-node communication handshakes and operational state resolutions.
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {orchestrationLogs.map((log, index) => {
                              const isInfo = log.type === 'info';
                              const isSuccess = log.type === 'success';
                              
                              return (
                                <div 
                                  key={index} 
                                  className={`p-2.5 rounded-lg border leading-relaxed select-text ${
                                    isInfo 
                                      ? 'bg-slate-900/40 border-slate-800/60 text-slate-400 text-[9px]'
                                      : isSuccess
                                        ? 'bg-emerald-950/15 border-emerald-500/20 text-emerald-300 text-[9px] font-bold'
                                        : 'bg-black border-slate-900 text-slate-300'
                                  }`}
                                >
                                  <div className="flex items-center justify-between border-b border-slate-950/60 pb-1 mb-1 text-[7px] text-slate-500 font-extrabold uppercase tracking-widest">
                                    <span className={isInfo ? "text-cyan-500/70" : isSuccess ? "text-emerald-400" : "text-emerald-500"}>
                                      {log.sender}
                                    </span>
                                    <span>{log.timestamp}</span>
                                  </div>
                                  <div className="uppercase tracking-wide">{log.text}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* PERFORMANCE GAIN STAT DECK */}
                  <div className="grid grid-cols-3 gap-3 bg-black/55 border border-slate-950 p-3 rounded-xl font-mono text-[8px] uppercase tracking-wider text-slate-400">
                    <div className="flex flex-col">
                      <span className="text-[7.5px] text-slate-500 font-bold">ESTIMATED CONVERSION</span>
                      <span className="text-medium text-emerald-400 font-bold mt-1">94.8% SLA</span>
                    </div>
                    <div className="flex flex-col border-l border-slate-950 pl-3">
                      <span className="text-[7.5px] text-slate-500 font-bold">COST ATTRIBUTION</span>
                      <span className="text-medium text-emerald-400 font-bold mt-1">ZERO-FUND / A2W</span>
                    </div>
                    <div className="flex flex-col border-l border-slate-950 pl-3">
                      <span className="text-[7.5px] text-slate-500 font-bold">SECURE DRIVE ARCHIVE</span>
                      <span className="text-medium text-emerald-400 font-bold mt-1 flex items-center gap-1 uppercase">
                        <Check className="w-3.5 h-3.5 text-emerald-400" /> CloudSync-02
                      </span>
                    </div>
                  </div>

                  {/* TRIGGER SIMULATION BUTTON */}
                  <div className="pt-2">
                    <button
                      disabled={isSimulatingSquad}
                      onClick={() => startSquadSimulation(selectedSquad.id)}
                      className={`w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-extrabold uppercase tracking-widest text-xs rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] ${
                        isSimulatingSquad ? 'opacity-50 cursor-not-allowed filter grayscale' : 'hover:scale-[1.005]'
                      }`}
                    >
                      {isSimulatingSquad ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin animate-spin-slow" />
                          <span>ORCHESTRATING ACTIVE TASKFORCE PROTOCOLS...</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 fill-black text-black ml-0.5" />
                          <span>INITIALIZE CONCURRENT TASKFORCE SIMULATION</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

        </div>
      </div>

      {/* CREATE NEW SQUAD MODAL */}
      <AnimatePresence>
        {showCreateSquadModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="bg-[#030612] border border-emerald-500/20 max-w-xl w-full rounded-2xl p-6 shadow-2xl relative space-y-4"
            >
              <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                <h4 className="text-white text-md font-mono font-black uppercase tracking-wider flex items-center gap-1.5">
                  <Plus className="w-5 h-5 text-emerald-400" />
                  <span>Commission New AI Specialist Squad</span>
                </h4>
                <button 
                  onClick={() => setShowCreateSquadModal(false)}
                  className="text-slate-500 hover:text-white uppercase font-mono text-xs"
                >
                  [Close]
                </button>
              </div>

              <div className="space-y-4 font-mono text-xs text-slate-400">
                {/* Squad Name */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Squad Designation Title</label>
                  <input
                    type="text"
                    placeholder="E.G., NATIONAL Client Resource Workflow AND INTEGRATION CORE"
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200 uppercase"
                    value={newSquadName}
                    onChange={(e) => setNewSquadName(e.target.value)}
                  />
                </div>

                {/* Description */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Strategic Operation Objective (Mission)</label>
                  <input
                    type="text"
                    placeholder="E.G., Combines marketing, qualify, and assist nodes into unified NexoraX billing records closures."
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200"
                    value={newSquadDesc}
                    onChange={(e) => setNewSquadDesc(e.target.value)}
                  />
                </div>

                {/* Tactical Objective prompt */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Cooperative Action Objective directives</label>
                  <textarea
                    rows={3}
                    placeholder="ENTER CRITICAL SYSTEM COMMAND DIRECTIVES COMPLIANT WITH Secure Payment Provider AND NEXORAX NEXORAX STRATEGY..."
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200 uppercase custom-scrollbar"
                    value={newSquadObjective}
                    onChange={(e) => setNewSquadObjective(e.target.value)}
                  />
                </div>

                {/* Multi-select Specialists list */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Select Squad Member Interlocks (Min 2)</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar bg-black/60 p-2.5 rounded-lg border border-slate-800/60">
                    {specialists.map(spec => {
                      const isChecked = newSquadMembers.includes(spec.id);
                      return (
                        <label 
                          key={spec.id} 
                          className="flex items-center gap-2 py-1 px-1.5 hover:bg-slate-900 rounded cursor-pointer select-none text-[10px] uppercase font-bold text-slate-300"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            className="accent-emerald-500 rounded border-slate-800 cursor-pointer text-emerald-500"
                            onChange={() => {
                              if (isChecked) {
                                setNewSquadMembers(prev => prev.filter(id => id !== spec.id));
                              } else {
                                setNewSquadMembers(prev => [...prev, spec.id]);
                              }
                            }}
                          />
                          <div className="flex items-center justify-between w-full">
                            <span>{spec.name}</span>
                            <span className="text-[7px] text-slate-500 font-mono scale-95 border border-slate-900 px-1 rounded bg-black">
                              {spec.roleKey}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-900">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSquadModal(false);
                    setNewSquadMembers([]);
                    setNewSquadName('');
                    setNewSquadDesc('');
                    setNewSquadObjective('');
                  }}
                  className="border border-slate-800 hover:border-slate-700 text-slate-400 font-mono text-xs uppercase px-4 py-2.5 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateSquad}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-mono font-black text-xs uppercase px-5 py-2.5 rounded-lg transition cursor-pointer font-extrabold"
                >
                  Deploy Squad Matrix
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CREATE NEW SPECIALIST MODAL */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="bg-[#030612] border border-cyan-500/20 max-w-xl w-full rounded-2xl p-6 shadow-2xl relative space-y-4"
            >
              <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                <h4 className="text-white text-md font-mono font-black uppercase tracking-wider flex items-center gap-1.5">
                  <Plus className="w-5 h-5 text-cyan-400" />
                  <span>Provisions New NexoraX Specialist Node</span>
                </h4>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-slate-500 hover:text-white uppercase font-mono text-xs"
                >
                  [Close]
                </button>
              </div>

              <div className="space-y-4 font-mono text-xs">
                {/* Spec Name */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Specialist Title/Role Name</label>
                  <input
                    type="text"
                    placeholder="E.G., LEAD MARKETING OPTIMIZATION DISPATCHER"
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200 uppercase"
                    value={newSpecName}
                    onChange={(e) => setNewSpecName(e.target.value)}
                  />
                </div>

                {/* Duty description */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Role Core Duty</label>
                  <input
                    type="text"
                    placeholder="E.G., Indexes business pipeline campaigns and drafts outbound scripts."
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200"
                    value={newSpecDuty}
                    onChange={(e) => setNewSpecDuty(e.target.value)}
                  />
                </div>

                {/* Base Voice selection */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">Default Acoustic Phonemic</label>
                  <select
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200 uppercase"
                    value={newSpecVoice}
                    onChange={(e) => setNewSpecVoice(e.target.value)}
                  >
                    {ACOUSTIC_VOICES.map(v => (
                      <option key={v.id} value={v.id}>{v.name} ({v.frequency})</option>
                    ))}
                  </select>
                </div>

                {/* Base prompt text */}
                <div className="flex flex-col space-y-1.5">
                  <label className="text-slate-400 uppercase tracking-wider font-bold">System Directive Instructions (Prisons/Rules)</label>
                  <textarea
                    rows={4}
                    placeholder="ENTER ABSOLUTE SYSTEM DIRECTIVES FOR CORE OPERATIONAL ALIGNMENT..."
                    className="bg-black border border-slate-800 rounded-lg p-2.5 text-slate-200 uppercase custom-scrollbar"
                    value={newSpecPrompt}
                    onChange={(e) => setNewSpecPrompt(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-900">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="border border-slate-800 hover:border-slate-700 text-slate-400 font-mono text-xs uppercase px-4 py-2.5 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateNewSpecialist}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-mono font-black text-xs uppercase px-5 py-2.5 rounded-lg transition"
                >
                  Deploy Specialist Node
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
